import type { QueryClient } from "@tanstack/react-query";
import {
  getGetHuntQueryKey,
  getListActiveHuntsQueryKey,
  getListMyFindsQueryKey,
  getListMapFindsQueryKey,
  getGetMyTeamQueryKey,
  getListInboxQueryKey,
} from "@workspace/api-client-react";
import { uploadHuntPhoto } from "./api";
import { clientLogger } from "./clientLogger";
import {
  listQueuedOfflineFinds,
  markQueuedAttempt,
  removeQueuedOfflineFind,
  type OfflineFindRecord,
} from "./offlineQueue";

// Phase F3 — sync coordinator that drains the offline find queue.
//
// Invariants this drain is built around:
//   - clientSubmissionId is generated ONCE at enqueue time and stored
//     with the record. Every drain attempt reuses that exact id, so the
//     server's partial unique index dedupes silently if a previous drain
//     attempt actually committed before the response was lost.
//   - The photo blob is also stored with the record (idb-keyval supports
//     Blob natively), so the drain re-uploads to object storage on each
//     attempt. The object-storage upload itself is NOT idempotent — but
//     a second copy of the same JPEG is a leak we can collect later, and
//     the find row only points to ONE objectPath (the latest successful
//     upload), so the player's experience stays clean.
//   - We never throw out of the drain loop; per-record failures are
//     captured via markQueuedAttempt and the loop continues.

const DRAIN_TIMEOUT_MS = 30_000; // per-record budget; keeps a wedged
// upload from blocking the rest of the queue.

async function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return await new Promise<T>((resolve, reject) => {
    const t = setTimeout(
      () => reject(new Error(`offline-sync: timed out after ${ms}ms`)),
      ms,
    );
    p.then((v) => {
      clearTimeout(t);
      resolve(v);
    }).catch((err) => {
      clearTimeout(t);
      reject(err);
    });
  });
}

// Outcome of a single drain attempt. The drain loop uses this to decide
// whether to evict a record (terminal failure → server will never accept
// it, draining forever wastes storage + bandwidth) or keep it for the
// next reconnect (transient failure → network blip, server hiccup).
type SubmitOutcome =
  | { kind: "ok" }
  | { kind: "terminal"; status: number; body: string }
  | { kind: "transient"; reason: string };

async function submitOne(record: OfflineFindRecord): Promise<SubmitOutcome> {
  let objectPath: string;
  try {
    const file = new File([record.fileBlob], record.fileName, {
      type: record.fileType,
    });
    objectPath = await uploadHuntPhoto(file);
  } catch (err) {
    // Photo upload failed — almost always a transient network error
    // (we got here because the queue is draining, which means we just
    // came back online; the next reconnect drain will retry).
    return {
      kind: "transient",
      reason: `upload failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
  // Use raw fetch (not the React Query mutation) — we run outside the
  // React render tree so the queue can drain on app mount, on `online`
  // events, and from a future Page Visibility callback. The endpoint
  // path mirrors the openapi-codegen client.
  let res: Response;
  try {
    res = await fetch(`/api/hunts/${record.huntId}/finds`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        objectPath,
        roomName: record.roomName ?? undefined,
        lat: record.lat,
        lng: record.lng,
        clientSubmissionId: record.id,
      }),
    });
  } catch (err) {
    return {
      kind: "transient",
      reason: `fetch threw: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
  if (res.ok) return { kind: "ok" };

  // Decision: which non-2xx statuses are terminal vs transient?
  //
  // Terminal (evict — replaying with the same payload + key will get the
  // same answer; storing the photo forever just wastes IndexedDB quota):
  //   400 bad request, 403 forbidden (player no longer on the team /
  //     hunt was deleted), 404 hunt-not-found, 409 conflict (server
  //     idempotency already accepted this find), 422 validation error.
  //
  // Transient (retain — re-auth, server hiccup, or rate-limit window
  // will clear and the next drain succeeds):
  //   401 unauthorized — session cookie expired while the photo sat in
  //     the queue. Player will sign in again on the next session and
  //     the queue should drain then. EVICTING here is a real data-loss
  //     path (the player took the photo, we silently drop it).
  //   408 request timeout, 425 too early, 429 too many requests — by
  //     definition transient.
  //   5xx — server-side fault, retry on next reconnect.
  //
  // Anything not explicitly classified is treated as transient (the
  // safer default for an offline queue: we'd rather leak a stale row
  // and let the user manually clear it than vanish their photo).
  const body = await res.text().catch(() => "");
  const TERMINAL_STATUSES = new Set([400, 403, 404, 409, 422]);
  if (TERMINAL_STATUSES.has(res.status)) {
    return { kind: "terminal", status: res.status, body: body.slice(0, 200) };
  }
  return {
    kind: "transient",
    reason: `POST returned ${res.status}: ${body.slice(0, 200)}`,
  };
}

export interface DrainResult {
  attempted: number;
  succeeded: number;
  failed: number;
}

export async function drainOfflineQueue(opts: {
  qc: QueryClient;
}): Promise<DrainResult> {
  const queue = await listQueuedOfflineFinds();
  let succeeded = 0;
  let failed = 0;
  for (const record of queue) {
    let outcome: SubmitOutcome;
    try {
      outcome = await withTimeout(submitOne(record), DRAIN_TIMEOUT_MS);
    } catch (err) {
      // Hit the per-record budget — treat as transient (the next
      // reconnect will retry; long uploads sometimes need a second
      // window).
      outcome = {
        kind: "transient",
        reason: err instanceof Error ? err.message : String(err),
      };
    }
    if (outcome.kind === "ok") {
      await removeQueuedOfflineFind(record.id);
      succeeded += 1;
    } else if (outcome.kind === "terminal") {
      // Server gave a definitive "no" — eviction prevents an infinite
      // drain loop that re-uploads the photo every reconnect. We log
      // loudly so a future telemetry hook can surface this to the
      // host without a silent data drop.
      clientLogger.error(
        `[offline-sync] terminal failure for ${record.id} (${outcome.status}): ${outcome.body} — evicting from queue`,
      );
      await removeQueuedOfflineFind(record.id);
      failed += 1;
    } else {
      clientLogger.error(
        `[offline-sync] transient failure for ${record.id}: ${outcome.reason}`,
      );
      // Server idempotency means re-submitting a server-known id is
      // safe — bump attempts so the UI can flag stuck records and a
      // future job can give up after N retries.
      await markQueuedAttempt(record.id, outcome.reason);
      failed += 1;
    }
  }
  if (succeeded > 0) {
    // Mirror the invalidation set the live submit handler runs.
    await Promise.all([
      opts.qc.invalidateQueries({ queryKey: getListActiveHuntsQueryKey() }),
      opts.qc.invalidateQueries({ queryKey: getListMyFindsQueryKey() }),
      opts.qc.invalidateQueries({ queryKey: getListMapFindsQueryKey() }),
      opts.qc.invalidateQueries({ queryKey: getGetMyTeamQueryKey() }),
      opts.qc.invalidateQueries({ queryKey: getListInboxQueryKey() }),
    ]);
    // Per-hunt invalidation: each drained record has its own huntId.
    const drainedHunts = new Set(queue.map((r) => r.huntId));
    await Promise.all(
      Array.from(drainedHunts).map((id) =>
        opts.qc.invalidateQueries({ queryKey: getGetHuntQueryKey(id) }),
      ),
    );
  }
  return { attempted: queue.length, succeeded, failed };
}
